import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import SaleCountdown from "@/components/SaleCountdown";
import type { ChartsData, ChartGame, TopGame } from "@/types/api";

function ChartSection({
  title, subtitle, icon, iconBg, games, showPlayers, showPrice, priceTbd,
}: {
  title: string; subtitle: string; icon: React.ReactNode; iconBg: string;
  games: ChartGame[]; showPlayers?: boolean; showPrice?: boolean; priceTbd?: string;
}) {
  return (
    <div className="bg-card-bg rounded-xl border border-gray-200 dark:border-card-border overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200 dark:border-card-border">
        <div className={`w-6 h-6 rounded-md ${iconBg} flex items-center justify-center text-white`}>{icon}</div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xs font-bold text-gray-900 dark:text-white">{title}</h2>
          <p className="text-[10px] text-gray-500 dark:text-sub-text truncate">{subtitle}</p>
        </div>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-card-border/50">
        {games.slice(0, 10).map((game, i) => (
          <Link key={`${game.appid}-${i}`} to={`/game/${game.appid}`} className="flex items-center gap-2 px-3 py-2 hover:bg-inner-bg dark:hover:bg-hover-bg transition-colors group">
            <span className={`shrink-0 w-5 text-center text-[11px] font-black tabular-nums ${i < 3 ? "text-amber-500" : "text-gray-400 dark:text-sub-text"}`}>{i + 1}</span>
            <div className="relative shrink-0 w-[60px] h-[28px] rounded overflow-hidden bg-hover-bg dark:bg-background">
              <img src={game.header_image || `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${game.appid}/header.jpg`} alt={game.name} width={60} height={28} className="w-full h-full object-cover" loading={i < 3 ? undefined : "lazy"} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-gray-900 dark:text-white line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{game.name}</p>
            </div>
            <div className="shrink-0 text-right">
              {showPlayers && game.current_players != null && (
                <div className="flex items-center gap-1">
                  <div className="w-1 h-1 rounded-full bg-emerald-500" />
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                    {game.current_players >= 1000 ? `${(game.current_players / 1000).toFixed(game.current_players >= 10000 ? 0 : 1)}K` : game.current_players.toLocaleString("ko-KR")}
                  </span>
                </div>
              )}
              {showPrice && (
                <div className="flex items-center gap-1">
                  {game.discount_percent > 0 && <span className="text-[9px] font-bold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-500/20 px-1 rounded">-{game.discount_percent}%</span>}
                  <span className={`text-[11px] font-bold ${game.is_free ? "text-emerald-600" : game.discount_percent > 0 ? "text-green-600 dark:text-green-400" : "text-gray-700 dark:text-gray-200"}`}>
                    {game.price || (priceTbd || "가격 미정")}
                  </span>
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function HomePage() {
  const { lang, t } = useLang();
  const { data: charts } = useFetch<ChartsData>(() => api.charts(lang) as Promise<ChartsData>, [lang]);
  const { data: topGamesRaw } = useFetch<TopGame[]>(async () => {
    const res = await api.topGames(lang) as { games: TopGame[] } | TopGame[];
    return Array.isArray(res) ? res : res.games || [];
  }, [lang]);

  const games = useMemo(() => topGamesRaw || [], [topGamesRaw]);
  const [selectedGame, setSelectedGame] = useState<TopGame | null>(null);
  const [recommendations, setRecommendations] = useState<TopGame[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGenre, setSelectedGenre] = useState("");

  useEffect(() => {
    if (!selectedGame || selectedGame.genres.length === 0 || games.length === 0) { setRecommendations([]); return; }
    const selectedGenres = new Set(selectedGame.genres);
    const scored = games
      .filter((g) => g.appid !== selectedGame.appid && g.genres.length > 0)
      .map((g) => ({ game: g, score: g.genres.filter((genre) => selectedGenres.has(genre)).length }))
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score || a.game.rank - b.game.rank)
      .slice(0, 5).map((s) => s.game);
    setRecommendations(scored);
  }, [selectedGame, games]);

  const allGenres = useMemo(() => {
    const s = new Set<string>();
    games.forEach((g) => g.genres.forEach((genre) => s.add(genre)));
    return Array.from(s).sort();
  }, [games]);

  const filteredGames = useMemo(() => {
    return games.filter((game) => {
      const matchSearch = searchQuery === "" || game.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchGenre = selectedGenre === "" || game.genres.includes(selectedGenre);
      return matchSearch && matchGenre;
    });
  }, [games, searchQuery, selectedGenre]);

  const totalPlayers = filteredGames.reduce((sum, g) => sum + g.current_players, 0);

  const genreScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkGenreScroll = useCallback(() => {
    const el = genreScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    checkGenreScroll();
    const el = genreScrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkGenreScroll, { passive: true });
    window.addEventListener("resize", checkGenreScroll);
    return () => { el.removeEventListener("scroll", checkGenreScroll); window.removeEventListener("resize", checkGenreScroll); };
  }, [checkGenreScroll, allGenres]);

  const scrollGenre = (dir: "left" | "right") => {
    genreScrollRef.current?.scrollBy({ left: dir === "left" ? -200 : 200, behavior: "smooth" });
  };

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6">
      {/* SaleCountdown — 원본과 동일 */}
      <div className="pt-3">
        <SaleCountdown />
      </div>

      {/* 실시간 동시접속자 통계 */}
      {games.length > 0 && (
        <div className="pb-2">
          <div className="text-center mb-4">
            <h1 className="text-sm font-medium text-gray-500 dark:text-muted mb-1">{t.home?.title || "Steam 실시간 동시접속"}</h1>
            <p className="text-3xl sm:text-5xl font-black text-gray-900 dark:text-white tabular-nums">
              {totalPlayers.toLocaleString("ko-KR")}
              <span className="text-sm sm:text-lg font-medium text-gray-400 dark:text-muted ml-1.5">{t.common?.players || "명"}</span>
            </p>
          </div>
        </div>
      )}

      {/* 4 Charts */}
      {charts && (
        <div className="space-y-6 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {charts.most_played.length > 0 && (
              <ChartSection title={t.home?.mostPlayed || "최다 플레이"} subtitle={t.home?.mostPlayedSub || "현재 가장 많은 플레이어"} icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>} iconBg="bg-emerald-600" games={charts.most_played} showPlayers />
            )}
            {charts.hot_releases.length > 0 && (
              <ChartSection title={t.home?.hotReleases || "핫 신작"} subtitle={t.home?.hotReleasesSub || "최근 화제의 신작"} icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2"/><path d="M12 6v6l4 2"/></svg>} iconBg="bg-orange-500" games={charts.hot_releases} showPrice priceTbd={t.common?.priceTbd} />
            )}
            {charts.popular_releases.length > 0 && (
              <ChartSection title={t.home?.popularReleases || "인기 출시"} subtitle={charts.popular_releases_month || t.home?.popularReleasesSub || "이번 달 인기 출시"} icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>} iconBg="bg-purple-600" games={charts.popular_releases} showPrice priceTbd={t.common?.priceTbd} />
            )}
            {charts.trending.length > 0 && (
              <ChartSection title={t.home?.trending || "트렌딩"} subtitle={t.home?.trendingSub || "급상승 중인 게임"} icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>} iconBg="bg-blue-600" games={charts.trending} showPrice priceTbd={t.common?.priceTbd} />
            )}
          </div>
        </div>
      )}

      {/* TOP 100 헤더 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-foreground flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white dark:text-background"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
          </div>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white">{(t.home?.top100 || "TOP {count}").replace("{count}", String(games.length))}</h2>
        </div>
      </div>

      {/* 검색 + 장르 필터 */}
      {games.length > 0 && (
        <div className="mb-3 flex flex-col gap-2">
          <div className="relative">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-muted pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input type="text" placeholder={t.home?.searchPlaceholder || "게임 검색..."} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full bg-card-bg border border-gray-200 dark:border-card-border rounded-xl pl-9 pr-4 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300 transition-colors" />
          </div>
          <div className="relative">
            {canScrollLeft && (
              <button onClick={() => scrollGenre("left")} className="absolute left-0 top-0 bottom-0 z-10 w-8 flex items-center justify-center bg-gradient-to-r from-background via-background/80 to-transparent">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-gray-600 dark:text-muted"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
            )}
            <div ref={genreScrollRef} className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide px-1">
              <button onClick={() => setSelectedGenre("")} className={`shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors ${selectedGenre === "" ? "bg-gray-900 text-white dark:bg-foreground dark:text-background" : "bg-card-bg border border-gray-200 dark:border-card-border text-gray-600 dark:text-muted hover:bg-inner-bg dark:hover:bg-hover-bg"}`}>{t.common?.all || "전체"}</button>
              {allGenres.map((genre) => (
                <button key={genre} onClick={() => setSelectedGenre(selectedGenre === genre ? "" : genre)} className={`shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors ${selectedGenre === genre ? "bg-gray-900 text-white dark:bg-foreground dark:text-background" : "bg-card-bg border border-gray-200 dark:border-card-border text-gray-600 dark:text-muted hover:bg-inner-bg dark:hover:bg-hover-bg"}`}>{genre}</button>
              ))}
            </div>
            {canScrollRight && (
              <button onClick={() => scrollGenre("right")} className="absolute right-0 top-0 bottom-0 z-10 w-8 flex items-center justify-center bg-gradient-to-l from-background via-background/80 to-transparent">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-gray-600 dark:text-muted"><path d="M9 18l6-6-6-6" /></svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Game List */}
      <div className="w-full pb-6">
        <div className="flex flex-col gap-1.5">
          {filteredGames.map((game) => (
            <div key={game.appid}>
              <div className={`w-full text-left group bg-card-bg rounded-xl border overflow-hidden hover:shadow-md transition-all duration-200 ${selectedGame?.appid === game.appid ? "border-blue-400 shadow-md ring-1 ring-blue-400/20" : "border-gray-200 dark:border-card-border hover:border-gray-300 dark:hover:border-card-border"}`}>
                <div className="flex items-center cursor-pointer" onClick={() => setSelectedGame(selectedGame?.appid === game.appid ? null : game)}>
                  <div className={`shrink-0 w-8 sm:w-11 flex items-center justify-center text-xs sm:text-base font-black tabular-nums ${game.rank <= 3 ? "text-amber-500" : game.rank <= 10 ? "text-gray-700 dark:text-foreground" : "text-gray-400 dark:text-muted"}`}>{game.rank}</div>
                  <div className="relative shrink-0 w-[80px] h-[37px] sm:w-[160px] sm:h-[60px] bg-hover-bg dark:bg-background overflow-hidden">
                    <img src={game.header_image || `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${game.appid}/header.jpg`} alt={game.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading={game.rank <= 3 ? undefined : "lazy"} />
                  </div>
                  <div className="flex-1 min-w-0 px-2 sm:px-3 py-1.5 flex items-center gap-2 sm:gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[11px] sm:text-sm text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors line-clamp-1">{game.name}</h3>
                      <div className="hidden sm:flex items-center gap-1.5 mt-0.5">
                        {game.genres.slice(0, 3).map((genre) => (
                          <span key={genre} className="text-[9px] text-gray-400 dark:text-muted bg-inner-bg px-1.5 py-0.5 rounded border border-inner-border dark:border-card-border font-medium">{genre}</span>
                        ))}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-[11px] sm:text-sm font-bold text-emerald-600 tabular-nums">{game.current_players.toLocaleString("ko-KR")}</p>
                      <p className="text-[9px] text-gray-400 dark:text-muted font-medium hidden sm:block">{t.home?.peak || "최고"} {game.peak_players.toLocaleString("ko-KR")}</p>
                    </div>
                  </div>
                </div>

                {selectedGame?.appid === game.appid && (
                  <div className="border-t border-inner-border dark:border-card-border bg-inner-bg/50 dark:bg-inner-bg">
                    <div className="flex flex-col sm:flex-row">
                      <div className="flex-1 min-w-0 p-3 sm:p-4">
                        {game.short_description && <p className="text-xs text-gray-500 dark:text-muted leading-relaxed line-clamp-3 mb-3">{game.short_description}</p>}
                        {game.peak_players > 0 && (
                          <div className="mb-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-gray-400 dark:text-sub-text">{t.home?.currentPeakPlayers || "현재 / 최고 접속"}</span>
                              <span className="text-[10px] font-bold text-gray-600 dark:text-muted tabular-nums">{Math.round((game.current_players / game.peak_players) * 100)}%</span>
                            </div>
                            <div className="h-1.5 bg-hover-bg dark:bg-background rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (game.current_players / game.peak_players) * 100)}%` }} />
                            </div>
                            <div className="flex justify-between mt-0.5">
                              <span className="text-[9px] text-emerald-600 dark:text-emerald-400 font-semibold tabular-nums">{game.current_players.toLocaleString("ko-KR")}</span>
                              <span className="text-[9px] text-gray-400 dark:text-sub-text tabular-nums">{game.peak_players.toLocaleString("ko-KR")}</span>
                            </div>
                          </div>
                        )}
                        {game.genres.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {game.genres.map((genre) => <span key={genre} className="text-[10px] bg-hover-bg dark:bg-background text-gray-600 dark:text-muted px-2 py-0.5 rounded-md font-medium">{genre}</span>)}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <Link to={`/game/${game.appid}`} className="text-center bg-gray-900 hover:bg-gray-800 text-white px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors">{t.common?.details || "상세 정보"}</Link>
                        </div>
                      </div>
                      {recommendations.length > 0 && (
                        <div className="shrink-0 sm:w-52 border-t sm:border-t-0 sm:border-l border-inner-border dark:border-card-border p-3">
                          <p className="text-[10px] font-bold text-gray-400 dark:text-muted uppercase tracking-wider mb-2">{t.home?.similarGames || "비슷한 게임"}</p>
                          <div className="flex sm:flex-col gap-2 overflow-x-auto sm:overflow-x-visible scrollbar-hide">
                            {recommendations.map((rec) => (
                              <button key={rec.appid} onClick={() => setSelectedGame(rec)} className="shrink-0 flex items-center gap-2 bg-card-bg rounded-lg border border-gray-200 dark:border-card-border p-1.5 pr-3 hover:bg-hover-bg transition-colors text-left">
                                <div className="relative w-[56px] h-[26px] rounded overflow-hidden bg-hover-bg dark:bg-background">
                                  <img src={rec.header_image || `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${rec.appid}/header.jpg`} alt={rec.name} className="w-full h-full object-cover" loading="lazy" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-[10px] font-semibold text-gray-900 dark:text-white line-clamp-1">{rec.name}</p>
                                  <p className="text-[9px] text-emerald-600 font-bold tabular-nums">{rec.current_players.toLocaleString("ko-KR")}명</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
